import { DrEntity } from './dr-entity';

export class PasswordAndUserLogoutPolicy extends DrEntity {
    auditLogUrl: string;
    enforceStrongPasswords: boolean;
    errorCode: string;
    extendedStatus: string;
    minimumLowercaseLetters: number;
    minimumNumbers: number;
    minimumPasswordLength: number;
    minimumSymbols: number;
    minimumUppercaseLetters: number;
    mustHaveCharacters: string;
    numberResults: number;
    passwordExpirationInDays: number;
    requestHandle: string;
    status: string;
    warningDetails: string;
    inactivityTimeoutInSeconds: number;
    historyLength: number;

    // Helping in .ts to make distinction between red exclamation triangle / green check
    showInvalid: boolean;

    // Helping in the decision of showing color: red OR green for password policy requirements
    minimumPasswordLengthMet: boolean;
    minimumUppercaseLettersMet: boolean;
    minimumLowercaseLettersMet: boolean;
    minimumNumbersMet: boolean;
    minimumSymbolsMet: boolean;

    constructor(value?: Partial<PasswordAndUserLogoutPolicy>) {
        super(value);
        if (value) {
            Object.assign(this, value);
        }
    }

    checkRequirements(password: string): void {
        this.minimumPasswordLengthMet = password.length >= this.minimumPasswordLength;
        const chars = [...password];
        let upperCount = 0, lowerCount = 0, numberCount = 0, symbolCount = 0;
        chars.forEach((thisChar, i) => {
            if (thisChar >= 'A' && thisChar <= 'Z') {
                upperCount++;
            } else if (thisChar >= 'a' && thisChar <= 'z') {
                lowerCount++;
            } else if (thisChar >= '0' && thisChar <= '9') {
                numberCount++;
            } else {
                symbolCount++;
            }
        });
        this.minimumUppercaseLettersMet = upperCount >= this.minimumUppercaseLetters;
        this.minimumLowercaseLettersMet = lowerCount >= this.minimumLowercaseLetters;
        this.minimumNumbersMet = numberCount >= this.minimumNumbers;
        this.minimumSymbolsMet = symbolCount >= this.minimumSymbols;
    }

    resetPasswordPolicyRequirements(): void {
        this.showInvalid = false;
        this.minimumPasswordLengthMet = false;
        this.minimumUppercaseLettersMet = false;
        this.minimumLowercaseLettersMet = false;
        this.minimumNumbersMet = false;
        this.minimumSymbolsMet = false;
    }

    isPasswordPolicyRequirementsForStandardAuthPassed(): boolean {
        return this.minimumPasswordLengthMet && this.minimumUppercaseLettersMet && this.minimumLowercaseLettersMet && this.minimumNumbersMet && this.minimumSymbolsMet;
    }
}

export class PasswordAndUserLogoutPolicyViewModel {
    static default = 0;

    model: Partial<PasswordAndUserLogoutPolicy>;
    original: Partial<PasswordAndUserLogoutPolicy>;

    constructor(value?: Partial<PasswordAndUserLogoutPolicy>) {
        if (value) {
            this.model = value;
        }

        if (this.model) {
            this.original = new PasswordAndUserLogoutPolicy(this.model);
        }
    }

    isDirty(): boolean {
        const dirty = false;
        if (this.model.enforceStrongPasswords !== this.original.enforceStrongPasswords) {
            return true;
        }

        if (this.model.minimumLowercaseLetters !== this.original.minimumLowercaseLetters) {
            return true;
        }

        if (this.model.minimumNumbers !== this.original.minimumNumbers) {
            return true;
        }

        if (this.model.minimumPasswordLength !== this.original.minimumPasswordLength) {
            return true;
        }

        if (this.model.minimumSymbols !== this.original.minimumSymbols) {
            return true;
        }

        if (this.model.minimumUppercaseLetters !== this.original.minimumUppercaseLetters) {
            return true;
        }

        if (this.model.mustHaveCharacters !== this.original.mustHaveCharacters) {
            return true;
        }

        if (this.model.numberResults !== this.original.numberResults) {
            return true;
        }

        if (this.model.passwordExpirationInDays !== this.original.passwordExpirationInDays) {
            return true;
        }
        if (this.model.inactivityTimeoutInSeconds !== this.original.inactivityTimeoutInSeconds) {
            return true;
        }

        return dirty;
    }
}
